import { useQuery } from "@noom/hive-core";
import { useMemo } from "react";

import { MarketplaceAdsDocument, MarketplaceAdsQuery } from "gql/graphql";

import { useAuthCredentials } from "../../../hooks/useAuthCredentials";

export const MARKETPLACE_PLACEMENT_GROUP_NAME = "marketplace";

type MarketplacePlacementResult =
  MarketplaceAdsQuery["placementGroupAds"]["placements"][number];

type MarketplaceContent = Extract<
  NonNullable<
    MarketplaceAdsQuery["placementGroupAds"]["placementGroupContent"]
  >,
  { __typename: "MarketplaceContent" }
>;

type MarketplaceAdResult = MarketplacePlacementResult["ads"][number];

export type MarketplaceAd = Omit<MarketplaceAdResult, "content"> & {
  content: Extract<
    NonNullable<MarketplaceAdResult["content"]>,
    { __typename: "AdCardContent" }
  >;
};

export type MarketplacePlacement = Omit<
  MarketplacePlacementResult,
  "ads" | "placementContent"
> & {
  ads: MarketplaceAd[];
  placementContent: Extract<
    NonNullable<MarketplacePlacementResult["placementContent"]>,
    { __typename: "CarouselSectionContent" }
  >;
};

export type UseMarketplaceAdsResult = {
  placements: MarketplacePlacement[];
  headerImageUrl: string | undefined;
  disclaimers: string[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

const hasCardContent = (ad: MarketplaceAdResult): ad is MarketplaceAd =>
  ad.content?.__typename === "AdCardContent";

/**
 * The placement-group-level marketplace content (header image + disclaimers).
 * `placementGroupContent` is a union that is null when unset, so narrow on the
 * `MarketplaceContent` variant before reading its fields.
 */
const getMarketplaceContent = (
  content: MarketplaceAdsQuery["placementGroupAds"]["placementGroupContent"]
): MarketplaceContent | undefined =>
  content?.__typename === "MarketplaceContent" ? content : undefined;

/**
 * A placement is renderable only when it has carousel section content
 * (title / card variant) and at least one ad with card content. Anything
 * else is dropped so the section can hide itself when nothing remains.
 */
const toRenderablePlacement = (
  placement: MarketplacePlacementResult
): MarketplacePlacement | undefined => {
  if (placement.placementContent?.__typename !== "CarouselSectionContent") {
    return undefined;
  }

  const ads = placement.ads.filter(hasCardContent);
  if (ads.length === 0) {
    return undefined;
  }

  return {
    ...placement,
    placementContent: placement.placementContent,
    ads,
  };
};

export const useMarketplaceAds = (): UseMarketplaceAdsResult => {
  const { accessCode } = useAuthCredentials();
  const shouldFetch = !!accessCode;

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
  } = useQuery(MarketplaceAdsDocument, {
    variables: {
      accessCode,
      placementGroupName: MARKETPLACE_PLACEMENT_GROUP_NAME,
    },
    skip: !shouldFetch,
  });

  const placements = useMemo(
    () =>
      (data?.placementGroupAds?.placements ?? [])
        .map(toRenderablePlacement)
        .filter(
          (placement): placement is MarketplacePlacement =>
            placement !== undefined
        ),
    [data]
  );

  const marketplaceContent = getMarketplaceContent(
    data?.placementGroupAds?.placementGroupContent
  );

  return {
    placements,
    headerImageUrl: marketplaceContent?.headerImageUrl,
    disclaimers: marketplaceContent?.disclaimers ?? [],
    loading: shouldFetch && queryLoading,
    error: data ? undefined : error,
    refetch: () => void refetch(),
  };
};
